import { useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import { api, ApiError } from "../api/client";
import { websiteUrl } from "../lib/websiteUrl";

interface Props {
  /** The signed-in user's email — typing it back is the confirmation gate. */
  email: string;
  plan: string;
  onClose: () => void;
}

/** Typed-confirmation dialog for permanent account deletion. The confirm button
 *  stays disabled until the email is typed exactly (case-insensitive), so this
 *  can never be completed by clicking through. */
export default function DeleteAccountModal({ email, plan, onClose }: Props) {
  const { signOut } = useClerk();
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase();

  const confirm = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAccount(typed.trim());
      // The Clerk account is already gone server-side; this just clears the
      // local session and sends them off the app.
      await signOut({ redirectUrl: websiteUrl() });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete your account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(36,28,18,0.45)] backdrop-blur-[2px] z-[400] flex items-center justify-center animate-fade-in"
      onClick={deleting ? undefined : onClose}
    >
      <div
        className="card max-w-[480px] w-[90vw] px-7 py-7 animate-scale-in max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-error">Delete your account</h3>
          <button
            className="bg-transparent border-none text-text-tertiary text-xl cursor-pointer leading-none transition-colors duration-200 hover:text-ink p-1 -mr-1 -mt-1"
            onClick={onClose}
            disabled={deleting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <p className="text-text-secondary text-[0.8125rem] leading-relaxed mb-3">
          This is permanent and cannot be undone. There is no recovery, no backup, and no grace
          period. Deleting the following, immediately:
        </p>

        <ul className="text-text-secondary text-[0.8125rem] leading-relaxed mb-3 list-disc pl-5 space-y-1">
          <li>Your sign-in account, so you can no longer log in</li>
          <li>Every practice session, transcript, and session report</li>
          <li>All logged errors, vocabulary, progress metrics, and exercises</li>
          <li>Any study materials you uploaded to your Library</li>
          <li>Your settings, practice schedule, and feedback history</li>
        </ul>

        {plan !== "trial" && (
          <p className="text-warning text-[0.8125rem] leading-relaxed mb-3">
            Your subscription is canceled at the same time. The rest of the paid period is not
            refunded, and any unused session-pack credits are lost.
          </p>
        )}

        <p className="text-text-tertiary text-[0.8125rem] leading-relaxed mb-4">
          Signing up again later starts a completely new, empty account.
        </p>

        <label className="text-sm font-semibold text-ink block mb-2">
          Type <span className="text-error font-mono">{email}</span> to confirm
        </label>
        <input
          type="text"
          className="input-base w-full text-base sm:text-sm"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder={email}
          value={typed}
          disabled={deleting}
          onChange={(e) => setTyped(e.target.value)}
        />

        {error && <p className="text-error text-xs mt-2">{error}</p>}

        <div className="flex justify-end gap-3 mt-5">
          <button className="btn-quiet text-sm" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            className="btn-primary text-sm px-5 !bg-error !text-cream disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={confirm}
            disabled={!matches || deleting}
          >
            {deleting ? "Deleting…" : "Delete my account permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
